import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  useForgotPassword,
  useLogin,
  useRegister,
  useResetPassword,
} from '../../api'
import { OnboardingStatus } from '../../App.constants'
import {
  ONBOARDING_STATUS_KEY,
  REFRESH_KEY,
  TOKEN_KEY,
  USER_DETAILS_KEY,
} from '../../components/providers/authProvider'
import { useNotification } from '../../components/providers/notification/useNotification'
import { setPermission } from '../../store/use-permission'
import { socketConnectNew } from '../socket'

const useAuth = (reset?: any) => {
  const { open: openNotification } = useNotification()
  const navigate = useNavigate()
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const to = queryParams.get("to");

  const [registerSuccess, setRegisterSuccess] = useState(false)

  const { mutate: login, isLoading: loadingLogin } = useLogin({
    onSuccess: (response: {
      data: {
        data: {
          tokens: { access_token: string; refresh_token: string }
          details: any
        }
      }
    }) => {
      const details = response?.data?.data?.details
      const onboardingStatus = details?.onboardingStatus

      if (onboardingStatus === OnboardingStatus.BLOCKED) {
        if (openNotification) {
          openNotification({
            key: 'login_blocked',
            message: 'Your account has been blocked.',
            description:
              'You cannot sign in. Please contact support for more information.',
            type: 'error',
          })
        }
        return
      }

      setPermission(true)
      if (openNotification) {
        openNotification?.({
          key: 'login_success',
          message: 'Login successfully!',
          type: 'success',
        })
      }
      localStorage.setItem(
        TOKEN_KEY,
        response.data.data.tokens['access_token']
      )
      localStorage.setItem(
        REFRESH_KEY,
        response.data.data.tokens['refresh_token']
      )
      localStorage.setItem(
        USER_DETAILS_KEY,
        JSON.stringify(response.data.data['details'])
      )
      localStorage.setItem(
        'status_user',
        details?.worldCheckVerification?.status ?? ''
      )
      if (onboardingStatus) {
        localStorage.setItem(ONBOARDING_STATUS_KEY, onboardingStatus)
      }
      socketConnectNew(response.data.data.tokens['access_token'])
      if (to) {
        navigate(`/${to}`)
      } else {
        navigate('/')
      }
    },
    onError: (error: { message: '' }) => {
      console.log('error', error)
      if (openNotification) {
        openNotification({
          key: 'login_failure',
          message: error.message || 'Login failed!',
          type: 'error',
        })
      }
    },
  })

  const { mutate: register, isLoading: loadingRegister } = useRegister({
    onSuccess: (response: { data: any }) => {
      setRegisterSuccess(true)
      reset()
    },
    onError: (error: { message: '' }) => {
      console.log('error', error)
      if (openNotification) {
        openNotification({
          key: 'register_failure',
          message: error.message || 'Register failed!',
          type: 'error',
        })
      }
    },
  })

  const { mutate: forgotPassword, isLoading: loadingForgotPassword } =
    useForgotPassword({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification?.({
            key: 'forgot_assword_success',
            message: response.data.message || 'Forgot Password successfully!',
            type: 'success',
          })
          navigate('/login')
        }
      },
      onError: (error: { message: '' }) => {
        console.log('error', error)
        if (openNotification) {
          openNotification({
            key: 'forgot_password_failure',
            message: error.message || 'Forgot Password failed!',
            type: 'error',
          })
        }
      },
    })

  const { mutate: resetPassword, isLoading: loadingResetPassword } =
    useResetPassword({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification?.({
            key: 'reset_assword_success',
            message: response.data.message || 'Reset Password successfully!',
            type: 'success',
          })
          navigate('/login')
        }
      },
      onError: (error: { message: '' }) => {
        console.log('error', error)
        if (openNotification) {
          openNotification({
            key: 'reset_password_failure',
            message: error.message || 'Reset Password failed!',
            type: 'error',
          })
        }
      },
    })

  return {
    login,
    loadingLogin,
    register,
    loadingRegister,
    forgotPassword,
    loadingForgotPassword,
    resetPassword,
    loadingResetPassword,
    registerSuccess,
    setRegisterSuccess,
  }
}

export default useAuth
